import Error from '@/components/error/Error';
import AdminPageLayout from '@/components/layout/AdminPageLayout';
import NavbarUnauth from '@/components/layout/NavbarUnauth';
import ExpiredLink from '@/components/onboarding/Errors/ExpiredLink';
import InvalidStatus from '@/components/onboarding/Errors/InvalidStatus';
import HeroBanner from '@/components/onboarding/HeroBanner';
import { useGetPharmacyOnboarding } from '@/hooks/onboarding/useGetPharmacyOnboarding';
import { usePortalPartnerBrand } from '@/hooks/portal-partner-brand';
import type { apiPharmacyContract } from '@montugroup/circuit-api-contracts';
import ProgressStepper from '@montugroup/circuit-shared/components/common/ProgressStepper';
import { Box, Container } from '@montugroup/design-system';
import { BrandThemeProvider } from '@montugroup/themes';
import { styled } from '@mui/material';
import type { ClientInferResponses } from '@ts-rest/core';
import { Outlet, useLocation, useParams } from 'react-router-dom';

const ErrorContainer = styled(Box)`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

type OnboardResponses = ClientInferResponses<typeof apiPharmacyContract.getPharmacyOnboarding>;

type OnboardError = Extract<OnboardResponses, { status: 403 }>['body'];

const PharmacyOnboard = () => {
  const { key: brandName } = usePortalPartnerBrand();
  const { token } = useParams();
  const { error } = useGetPharmacyOnboarding({ token });
  const location = useLocation();
  const stepOrder = [`/onboard/${token}`, `/onboard/${token}/success`];
  const currentStepIndex = stepOrder.indexOf(location.pathname);

  if (error) {
    const err = error as { body: OnboardError };

    const errorCode = err.body.error.code;
    if (errorCode) {
      return (
        <BrandThemeProvider theme={brandName}>
          <NavbarUnauth />
          <Container>{errorCode === 'Link Expired' ? <ExpiredLink /> : <InvalidStatus />}</Container>
        </BrandThemeProvider>
      );
    }
    return (
      <ErrorContainer>
        <Error {...error} />
      </ErrorContainer>
    );
  }

  return (
    <BrandThemeProvider theme={brandName}>
      <NavbarUnauth />
      <HeroBanner />
      <Container>
        <AdminPageLayout title="Pharmacy Network Onboarding" themeVariant={brandName}>
          <ProgressStepper currentStepIndex={currentStepIndex} steps={['Onboarding form', "You're done!"]} />
          <Outlet />
        </AdminPageLayout>
      </Container>
    </BrandThemeProvider>
  );
};

export default PharmacyOnboard;
